import { BaseFormElement, IBaseFormElementProps } from "./BaseFormElement";
import { TypedValueValidator } from "./Validation/TypedValueValidator";
import { IValidationCondition } from "./Validation/IValidationCondition";
import * as React from "react";
import { ObjectComparer } from "../Utils/ObjectComparer";
import { isNotEmpty, maxLength, minLength } from "./Validation/RawValueRules";
import { isNullOrEmpty } from "../Utils/Arrays";
import * as $ from "jquery";
import { debounce } from "lodash";

export interface IBaseTextBoxProps<TType> extends IBaseFormElementProps {
    value?: TType;
    onChange?: (newValue: TType) => void;
    onBlur?: (newValue: TType) => void;
    onBlurInvalid?: () => void;
    onFocus?: (e: any) => void;
    onClick?: (e: React.SyntheticEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    disabled?: boolean;
    readOnly?: boolean;
    disableCopyPaste?: boolean;
    required?: string;
    maxLength?: number;
    maxLengthValidation?: IValidationCondition<number>;
    minLengthValidation?: IValidationCondition<number>;
    placeholder?: string;
    id?: string;
    cssInput?: string;
    autocomplete?: boolean;
    tabIndex?: number;
    ariaLabel?: string;
    ariaLabelledby?: string;
    ariaDescribedby?: string;
    ariaInvalidCanBeShown?: boolean;
    ariaInvalidShouldBeShown?: boolean;
    allowOnlyParsableCharacters?: boolean;
    onRef?: (ref: HTMLInputElement) => void;
    validateOnTyping?: boolean;
    allowedCharacters?: RegExp;
    runValidationOnMount?: boolean;
    alwaysValidateOnUpdate?: boolean;
    deferValidationAdditionUntilCalled?: boolean;
    inputMode?: "text" | "decimal" | "date" | "numeric" | "tel" | "email" | "url" | "search";

    textAlign?: "left" | "right" | "center";
    textCase?: "upper" | "lower";
    textLength?: number;

    notAllowPaste?: boolean;

    isDebounce?: boolean;
    isFromSell?: boolean;
    runOnChangeOnUpdateing?: boolean;
}

export interface IBaseTextBoxState {
    value?: string;
    pristineValue?: any;
    isValid?: boolean;
    isPristine?: boolean;
    disable?: boolean;
}

// TODO: straslivy hack "IBaseTextBoxProps<any>"
export abstract class BaseTextBox<TType, TProps extends IBaseTextBoxProps<any>> extends BaseFormElement<
    TProps,
    IBaseTextBoxState
> {
    protected validator: TypedValueValidator<string, TType>;

    protected inputRef: HTMLInputElement;

    protected isChangeEventDisabled = false;

    private _isChangePropsId = true;
  
    constructor(props: TProps) {
        super(props);
        this.state = this.getDefaultState(props);
        
        this.onDebounce = debounce(this.onDebounce, 1000);

        this.validator = new TypedValueValidator<string, TType>((val) => this.parseValue(val));
        this.initRawValidation();
        this.initParsedValidation();

        if (!props.onChange && !props.onBlur) {
            // tslint:disable-next-line:no-console
            console.warn('Input "onBlur" and "onChange" are both not set. Validation will not be triggered', this);
        }
    }

    onDebounce = (v) => this.props.onChange(v);

    public setDisable() {
        this.setState({ disable: true });
    }

    public isDisabled(): boolean {
        return this.state.disable || this.props.disabled;
    }

    public render(): JSX.Element {
        const autocompleteAttribute: any = {};
        const maxLengthAttribute: any = {};
        const tabIndexAttribute: any = {};
        const copyPaste: any = {};

        autocompleteAttribute.autoComplete = this.props.autocomplete ? "on" : "off";

        if (this.props.maxLength !== undefined && this.props.maxLength !== null) {
            maxLengthAttribute.maxLength = this.props.maxLength;
        }

        if (this.props.tabIndex !== undefined && this.props.tabIndex !== null) {
            tabIndexAttribute.tabIndex = this.props.tabIndex;
        }

        if (this.props.notAllowPaste) {
            copyPaste.onCopy = (e) => e.preventDefault();
            copyPaste.onPaste = (e) => e.preventDefault();
            copyPaste.onContextMenu = (e) => e.preventDefault();
            copyPaste.onCut = (e) => e.preventDefault();
        }

        const valueToDisplay = this.getDisplayValue();
        return (
            <input
                type="text"
                inputMode={this.props.inputMode}
                className={
                    this.props.cssInput || `form-input${this.props.textAlign ? ` text-${this.props.textAlign}` : ""}`
                }
                value={valueToDisplay}
                disabled={this.isDisabled()}
                readOnly={this.props.readOnly}
                id={this.props.id}
                placeholder={this.props.placeholder}
                onChange={() => this.onChange()}
                onBlur={() => this.onBlur()}
                onFocus={(event: React.SyntheticEvent) => this.onFocus(event)}
                onKeyDown={(event: React.KeyboardEvent) => this.onKeyDown(event)}
                onKeyPress={(event: React.KeyboardEvent) => this.onKeyPress(event)}
                onClick={(event: React.SyntheticEvent) => this.onClick(event)}
                ref={(input: HTMLInputElement) => this.setRef(input)}
                aria-label={this.props.ariaLabel}
                aria-labelledby={this.props.ariaLabelledby}
                aria-describedby={((!this.state.isValid || this.props.ariaInvalidShouldBeShown) && this.props.ariaInvalidCanBeShown) ? this.props.ariaDescribedby : ""}
                aria-invalid={
                    (!this.state.isValid || this.props.ariaInvalidShouldBeShown) && this.props.ariaInvalidCanBeShown
                }
                {...maxLengthAttribute}
                {...autocompleteAttribute}
                {...tabIndexAttribute}
                {...copyPaste}
            />
        );
    }

    public componentWillReceiveProps(nextProps: TProps): void {
        // If the value passed has changed, set it. If value is not passed it will
        // internally update, and this will never run
        if (
            !ObjectComparer.isSame(this.props.value, nextProps.value) &&
            !ObjectComparer.isSame(nextProps.value, this.parseValue(this.state.value))
        ) {
            this.setValue(this.formatDisplayValue(nextProps.value));
        }
    }

    public componentDidUpdate(prevProps: TProps): void {
        const _validateOnUpdate = !this._isChangePropsId && this.props.alwaysValidateOnUpdate !== false;
        if (
            (!ObjectComparer.isSame(this.props.value, prevProps.value) &&
                (this.props.validateOnTyping ||
                    !ObjectComparer.isSame(this.props.value, this.parseValue(this.state.value))) &&
                !this.state.isPristine) ||
            _validateOnUpdate
        ) {
            this.runValidation(this.state.value, this.props.validateOnTyping);
        }
        if (this.props.runOnChangeOnUpdateing && (!ObjectComparer.isSame(this.props.value, prevProps.value) || this.props.isFromSell)) {
            this.runOnChange(this.state.value);
        }
    }

    public shouldComponentUpdate(nextProps: TProps, nextState: IBaseTextBoxState): boolean {
        this._isChangePropsId = nextProps.id !== this.props.id;
        return !ObjectComparer.isSame(this.props, nextProps) || !ObjectComparer.isSame(this.state, nextState);
    }

    public componentDidMount(): void {
        if (this.props.runValidationOnMount) {
            this.completelyRecalculateValidation();
        }

        if (this.props.disableCopyPaste) {
            $(this.inputRef).bind("cut copy paste", (e: any) => e.preventDefault());
        }
    }

    public isValid(): boolean {
        const value = this.inputRef.value;
        return this.runValidation(value, true, true);
    }

    protected abstract isParsable(input: string): boolean;

    protected abstract parseValue(input: string): TType;

    protected formatDisplayValue(value: TType): string {
        if (value === null || value === undefined) {
            return "";
        }

        return value.toString();
    }

    protected getDisplayValue(): string {
        return this.state.value;
    }

    protected initRawValidation(): void {
        this.validator.rawValidator.clear();

        this.validator.rawValidator.add(isNotEmpty, this.props.required);
        this.validator.rawValidator.addFromCondition(this.props.maxLengthValidation, maxLength);
        this.validator.rawValidator.addFromCondition(this.props.minLengthValidation, minLength);
    }

    protected initParsedValidation(): void {
        this.validator.parsedValidator.clear();
    }

    protected runValidation(
        rawValue: string,
        validationTriggered?: boolean,
        completelyRecalculateValidation?: boolean
    ): boolean {
        if ($(this.inputRef).is(":hidden")) {
            return true;
        }

        this.initRawValidation();
        this.initParsedValidation();
        let errors;
        if (this.props.deferValidationAdditionUntilCalled && !completelyRecalculateValidation) {
            errors = this.validator.getUnresolvedValidationErrors(rawValue);
        } else {
            errors = this.validator.getValidationErrors(rawValue);
        }
        this.triggerValidationResultChange(errors, validationTriggered);
        const isValid = isNullOrEmpty(errors);

        if (isValid !== this.state.isValid) {
            this.setState({ isValid });
        }
        return isValid;
    }

    public completelyRecalculateValidation(): boolean {
        const value = this.inputRef.value;
        return this.runValidation(value, this.props.validateOnTyping, true);
    }

    protected setRef(input: HTMLInputElement): void {
        this.inputRef = input;
        if (this.props.onRef) {
            this.props.onRef(input);
        }
    }

    private getDefaultState(props: TProps): IBaseTextBoxState {
        return {
            value: this.formatDisplayValue(props.value),
            isValid: true,
            isPristine: true,
            pristineValue: props.value,
        };
    }

    protected setValue(value: string): void {
        if (this.props.allowOnlyParsableCharacters && !this.isParsable(value)) {
            return;
        }

        this.setState({
            value,
            isPristine: false,
        });
    }

    protected onChange(): any {
        if (this.isChangeEventDisabled) {
            return;
        }
        let value = this.inputRef.value;
        if (this.props.textLength) {value = value.substr(0, this.props.textLength);}
        if (this.props.textCase) {value = this.props.textCase == "upper" ? value.toUpperCase() : value.toLowerCase();}

        this.setValue(value);
        this.runOnChange(value);
    }

    protected runOnChange(value) {
        if (this.props.onChange) {
            const isValid = this.runValidation(value, this.props.validateOnTyping);
            if (isValid || this.isParsable(value)) {
                const parsedValue = this.parseValue(value);
                this.props.isDebounce ? this.onDebounce(parsedValue) : this.props.onChange(parsedValue);
            }
        }
    }

    protected onBlur(): any {
        const value = this.inputRef.value;

        if (this.props.onBlur) {
            const isValid = this.runValidation(value, true);
            if (isValid || this.isParsable(value)) {
                const parsedValue = this.parseValue(value);
                this.props.onBlur(parsedValue);
            }
        }

        if (this.props.onBlurInvalid) {
            const isValid = this.runValidation(value, true);
            if (!isValid && !this.isParsable(value)) {
                this.props.onBlurInvalid();
            }
        }
    }

    private onFocus(event: React.SyntheticEvent): any {
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    }

    protected onKeyDown(event: React.KeyboardEvent): void {
        if (this.props.allowedCharacters && this.isControlKey(event)) {
            this.isChangeEventDisabled = false;
        }

        if (this.props.onKeyDown) {
            this.props.onKeyDown(event);
        }
    }

    private onKeyPress(event: React.KeyboardEvent): void {
        if (this.props.allowedCharacters && this.isCharacterDisabled(event)) {
            this.isChangeEventDisabled = true;
            return;
        }

        this.isChangeEventDisabled = false;
    }

    private isCharacterDisabled(event: React.KeyboardEvent): boolean {
        if (this.props.allowedCharacters) {
            const typedCharacter = String.fromCharCode(event.which || event.charCode || 0);
            const isCharacterDisabled = typedCharacter.search(this.props.allowedCharacters) === -1;

            return isCharacterDisabled;
        }

        return false;
    }

    private onClick(event: React.SyntheticEvent): void {
        if (this.props.onClick) {
            this.props.onClick(event);
        }
    }

    private isControlKey(event: React.KeyboardEvent): boolean {
        // list of key codes: https://www.cambiaresearch.com/articles/15/javascript-char-codes-key-codes
        return event.keyCode >= 8 && event.keyCode <= 46;
    }
}
